import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { StudioMeasureFieldConfig } from '../studio-measure-input/studio-measure-input.config';
import { StudioMeasureInputComponent } from '../studio-measure-input/studio-measure-input.component';

type StudioSpaceInputKind = 'box' | 'gap' | 'corners';
type StudioSpaceInputMode = 'all' | 'axis' | 'detailed';

@Component({
  selector: 'app-studio-space-input',
  standalone: true,
  imports: [CommonModule, StudioMeasureInputComponent],
  templateUrl: './studio-space-input.component.html',
  styleUrl: './studio-space-input.component.scss'
})
export class StudioSpaceInputComponent implements OnChanges {
  @Input() value = '';
  @Input({ required: true }) config!: StudioMeasureFieldConfig;
  @Input({ required: true }) kind!: StudioSpaceInputKind;
  @Output() valueChange = new EventEmitter<string>();

  activeMode: StudioSpaceInputMode = 'all';
  private preferredMode: StudioSpaceInputMode | null = null;
  allValue = '';
  axisPrimary = '';
  axisSecondary = '';
  detailTop = '';
  detailRight = '';
  detailBottom = '';
  detailLeft = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['kind']) {
      this.syncFromValue();
    }
  }

  get modeOptions(): Array<{ id: StudioSpaceInputMode; label: string }> {
    switch (this.kind) {
      case 'gap':
        return [
          { id: 'all', label: 'All' },
          { id: 'axis', label: 'Axis' }
        ];
      case 'corners':
        return [
          { id: 'all', label: 'All' },
          { id: 'detailed', label: 'Corners' }
        ];
      default:
        return [
          { id: 'all', label: 'All' },
          { id: 'axis', label: 'X/Y' },
          { id: 'detailed', label: 'Sides' }
        ];
    }
  }

  get axisPrimaryLabel(): string {
    return this.kind === 'gap' ? 'Row' : 'Y';
  }

  get axisSecondaryLabel(): string {
    return this.kind === 'gap' ? 'Column' : 'X';
  }

  get detailedLabels(): string[] {
    return this.kind === 'corners'
      ? ['Top left', 'Top right', 'Bottom right', 'Bottom left']
      : ['Top', 'Right', 'Bottom', 'Left'];
  }

  setMode(mode: StudioSpaceInputMode): void {
    if (this.activeMode === mode) {
      return;
    }

    this.preferredMode = mode;
    this.activeMode = mode;
    this.emitCurrentValue();
  }

  updateAll(value: string): void {
    this.allValue = value;
    this.axisPrimary = value;
    this.axisSecondary = value;
    this.detailTop = value;
    this.detailRight = value;
    this.detailBottom = value;
    this.detailLeft = value;
    this.emitCurrentValue();
  }

  updateAxisPrimary(value: string): void {
    this.axisPrimary = value;
    if (this.kind === 'gap') {
      this.detailTop = value;
      this.detailBottom = value;
    } else {
      this.detailTop = value;
      this.detailBottom = value;
    }
    this.emitCurrentValue();
  }

  updateAxisSecondary(value: string): void {
    this.axisSecondary = value;
    if (this.kind === 'gap') {
      this.detailRight = value;
      this.detailLeft = value;
    } else {
      this.detailRight = value;
      this.detailLeft = value;
    }
    this.emitCurrentValue();
  }

  updateDetailed(index: number, value: string): void {
    switch (index) {
      case 0:
        this.detailTop = value;
        break;
      case 1:
        this.detailRight = value;
        break;
      case 2:
        this.detailBottom = value;
        break;
      case 3:
        this.detailLeft = value;
        break;
      default:
        return;
    }

    this.emitCurrentValue();
  }

  private syncFromValue(): void {
    const tokens = this.value.trim().split(/\s+/).filter(Boolean);
    switch (this.kind) {
      case 'gap':
        this.syncGap(tokens);
        break;
      case 'corners':
        this.syncCorners(tokens);
        break;
      default:
        this.syncBox(tokens);
        break;
    }
  }

  private syncBox(tokens: string[]): void {
    const [top, right, bottom, left] = this.expandBoxTokens(tokens);
    this.detailTop = top;
    this.detailRight = right;
    this.detailBottom = bottom;
    this.detailLeft = left;
    this.axisPrimary = top;
    this.axisSecondary = right;
    this.allValue = top;
    this.activeMode = this.resolveMode(
      top === right && right === bottom && bottom === left ? 'all' : null,
      top === bottom && right === left ? 'axis' : null,
      'detailed'
    );
  }

  private syncGap(tokens: string[]): void {
    const [row, column] = this.expandGapTokens(tokens);
    this.allValue = row;
    this.axisPrimary = row;
    this.axisSecondary = column;
    this.detailTop = row;
    this.detailRight = column;
    this.detailBottom = row;
    this.detailLeft = column;
    this.activeMode = this.resolveMode(row === column ? 'all' : null, 'axis', 'axis');
  }

  private syncCorners(tokens: string[]): void {
    const [topLeft, topRight, bottomRight, bottomLeft] = this.expandCornerTokens(tokens);
    this.detailTop = topLeft;
    this.detailRight = topRight;
    this.detailBottom = bottomRight;
    this.detailLeft = bottomLeft;
    this.axisPrimary = topLeft;
    this.axisSecondary = topRight;
    this.allValue = topLeft;
    this.activeMode = this.resolveMode(
      topLeft === topRight && topRight === bottomRight && bottomRight === bottomLeft ? 'all' : null,
      null,
      'detailed'
    );
  }

  private emitCurrentValue(): void {
    switch (this.activeMode) {
      case 'all':
        this.valueChange.emit(this.allValue.trim());
        return;
      case 'axis':
        if (this.kind === 'gap') {
          this.valueChange.emit(this.joinTokens([this.axisPrimary, this.axisSecondary]));
          return;
        }

        this.valueChange.emit(this.joinTokens([this.axisPrimary, this.axisSecondary]));
        return;
      case 'detailed':
        this.valueChange.emit(this.joinTokens([this.detailTop, this.detailRight, this.detailBottom, this.detailLeft]));
        return;
      default:
        this.valueChange.emit(this.value);
    }
  }

  private expandBoxTokens(tokens: string[]): [string, string, string, string] {
    if (!tokens.length) {
      return ['', '', '', ''];
    }

    if (tokens.length === 1) {
      return [tokens[0], tokens[0], tokens[0], tokens[0]];
    }

    if (tokens.length === 2) {
      return [tokens[0], tokens[1], tokens[0], tokens[1]];
    }

    if (tokens.length === 3) {
      return [tokens[0], tokens[1], tokens[2], tokens[1]];
    }

    return [tokens[0], tokens[1], tokens[2], tokens[3]];
  }

  private expandGapTokens(tokens: string[]): [string, string] {
    if (!tokens.length) {
      return ['', ''];
    }

    if (tokens.length === 1) {
      return [tokens[0], tokens[0]];
    }

    return [tokens[0], tokens[1]];
  }

  private expandCornerTokens(tokens: string[]): [string, string, string, string] {
    if (!tokens.length) {
      return ['', '', '', ''];
    }

    if (tokens.length === 1) {
      return [tokens[0], tokens[0], tokens[0], tokens[0]];
    }

    if (tokens.length === 2) {
      return [tokens[0], tokens[1], tokens[0], tokens[1]];
    }

    if (tokens.length === 3) {
      return [tokens[0], tokens[1], tokens[2], tokens[1]];
    }

    return [tokens[0], tokens[1], tokens[2], tokens[3]];
  }

  private joinTokens(tokens: string[]): string {
    return tokens.map((token) => token.trim()).filter(Boolean).join(' ');
  }

  private resolveMode(allMode: StudioSpaceInputMode | null, axisMode: StudioSpaceInputMode | null, fallback: StudioSpaceInputMode): StudioSpaceInputMode {
    const allowedModes = new Set(this.modeOptions.map((option) => option.id));
    if (this.preferredMode && allowedModes.has(this.preferredMode)) {
      return this.preferredMode;
    }

    if (allMode && allowedModes.has(allMode)) {
      return allMode;
    }

    if (axisMode && allowedModes.has(axisMode)) {
      return axisMode;
    }

    return fallback;
  }
}
